import {
  CREATOR_SCRIPT_JOB_STATUS,
  useGetCreatorScriptGeneratorJob,
  useSaveCreatorScript,
} from "@entities/aiWorkspace";
import { deriveContentAnalysis } from "@features/aiScriptGeneration/lib/deriveContentAnalysis";
import { ToastService } from "@shared/lib/services";
import { RefObject, useEffect, useRef, useState } from "react";
import { useDebounce } from "use-debounce";
import { installBackgroundGenerationClock } from "./backgroundGenerationClock";
import { finishedScript, holdScriptGeneration, rememberFinishedScript } from "./finishedScript";
import { ScriptEditorHandle } from "./ScriptEditor";
import { isSchemaScriptHtml } from "./scriptDocument";
import {
  DEFAULT_SCRIPT_SETTINGS,
  SCRIPT_DURATIONS,
  SCRIPT_FORMATS,
  SCRIPT_LANGUAGES,
  SCRIPT_TONES,
  ScriptDraft,
  matchedScriptOption,
} from "./types";

function scriptText(value: unknown) {
  return typeof value === "string" ? value : "";
}

export function useScriptGenerationSession(
  active: ScriptDraft,
  editorRef: RefObject<ScriptEditorHandle | null>,
  onChange: (patch: Partial<Pick<ScriptDraft, "brief" | "settings" | "markdown" | "status">>) => void,
  onGeneratingChange?: (generating: boolean) => void
) {
  const savedScript = useRef("");
  const seededKey = useRef("");
  const markdownRef = useRef(active.markdown);
  const onChangeRef = useRef(onChange);
  const jobIdRef = useRef(active.creator.jobId);
  const mounted = useRef(true);
  const onGeneratingChangeRef = useRef(onGeneratingChange);
  onGeneratingChangeRef.current = onGeneratingChange;
  const [generating, setGenerating] = useState(false);
  const [localReady, setLocalReady] = useState(false);
  const [pickedConceptId, setPickedConceptId] = useState("");
  markdownRef.current = active.markdown;
  onChangeRef.current = onChange;
  jobIdRef.current = active.creator.jobId;
  const { job, isLoading, refetch } = useGetCreatorScriptGeneratorJob(active.creator.jobId);
  const { save, saveNow } = useSaveCreatorScript();
  const [debouncedMarkdown] = useDebounce(active.markdown, 800);
  const status = job?.status;
  const ready = status === CREATOR_SCRIPT_JOB_STATUS.ready;
  const waitingInputs = status === CREATOR_SCRIPT_JOB_STATUS.waitingUserInputs;
  const failed = status === CREATOR_SCRIPT_JOB_STATUS.failed;
  const analyzing =
    (!job && isLoading) || status === CREATOR_SCRIPT_JOB_STATUS.waitingCreatorProfile;

  useEffect(() => {
    mounted.current = true;
    const finished = finishedScript(jobIdRef.current);
    if (finished && !isSchemaScriptHtml(markdownRef.current)) {
      savedScript.current = finished;
      onChangeRef.current({ markdown: finished, status: "ready" });
    }
    return () => {
      mounted.current = false;
    };
  }, [active.creator.jobId]);

  useEffect(() => {
    seededKey.current = "";
    savedScript.current = "";
    setGenerating(false);
    setLocalReady(false);
    setPickedConceptId("");
  }, [active.creator.jobId]);

  useEffect(() => {
    if (!job) return;
    if (job._id !== active.creator.jobId && job.id !== active.creator.jobId) return;
    const key = `${job._id}:${job.status}`;
    if (seededKey.current === key) return;
    seededKey.current = key;
    if (job.userInputs) {
      onChangeRef.current({
        brief: scriptText(job.userInputs.brief),
        settings: {
          format: matchedScriptOption(job.userInputs.format, SCRIPT_FORMATS, DEFAULT_SCRIPT_SETTINGS.format),
          duration: matchedScriptOption(
            job.userInputs.duration,
            SCRIPT_DURATIONS.map((item) => item.value),
            DEFAULT_SCRIPT_SETTINGS.duration
          ),
          tone: matchedScriptOption(job.userInputs.toneBalance, SCRIPT_TONES, DEFAULT_SCRIPT_SETTINGS.tone),
          language: matchedScriptOption(job.userInputs.language, SCRIPT_LANGUAGES, DEFAULT_SCRIPT_SETTINGS.language),
        },
      });
    }
    if (job.status !== CREATOR_SCRIPT_JOB_STATUS.ready) return;
    const serverScript = scriptText(job.script);
    if (!isSchemaScriptHtml(serverScript)) return;
    if (!markdownRef.current.trim() || !isSchemaScriptHtml(markdownRef.current)) {
      savedScript.current = serverScript;
      onChangeRef.current({ markdown: serverScript, status: "ready" });
      return;
    }
    if (markdownRef.current === serverScript) savedScript.current = serverScript;
  }, [active.creator.jobId, job]);

  const canPersist = ready || localReady;

  useEffect(() => {
    if (!canPersist || generating) return;
    if (debouncedMarkdown !== active.markdown) return;
    if (!debouncedMarkdown.trim() || debouncedMarkdown === savedScript.current) return;
    const jobId = active.creator.jobId;
    savedScript.current = debouncedMarkdown;
    save({ creatorScriptGeneratorJobId: jobId, script: debouncedMarkdown });
  }, [active.creator.jobId, active.markdown, canPersist, debouncedMarkdown, generating, save]);

  const generate = async (scriptConceptId: string) => {
    const jobId = active.creator.jobId;
    setPickedConceptId(scriptConceptId);
    setGenerating(true);
    holdScriptGeneration(true);
    onGeneratingChangeRef.current?.(true);
    onChange({ status: "generating" });
    const stillHere = () => mounted.current && jobId === jobIdRef.current;
    const restoreClock = installBackgroundGenerationClock();
    try {
      let editor = editorRef.current;
      for (let attempt = 0; attempt < 30 && !editor; attempt += 1) {
        await new Promise<void>((resolve) => {
          window.requestAnimationFrame(() => resolve());
        });
        editor = editorRef.current;
      }
      const markdown = await editor?.generate(scriptConceptId);
      if (!stillHere()) {
        onChange({ status: "idle" });
        return;
      }
      if (!markdown?.trim()) {
        ToastService.error("Failed to generate script");
        onChange({ status: "idle" });
        return;
      }
      savedScript.current = markdown;
      rememberFinishedScript(jobId, markdown);
      onChange({ markdown, status: "ready" });
      setLocalReady(true);
      await saveNow({ creatorScriptGeneratorJobId: jobId, script: markdown });
    } catch {
      if (stillHere() && !finishedScript(jobId)) ToastService.error("Failed to generate script");
      if (stillHere() && !finishedScript(jobId)) onChange({ status: "idle" });
    } finally {
      restoreClock();
      holdScriptGeneration(false);
      if (stillHere()) {
        setGenerating(false);
        onGeneratingChangeRef.current?.(false);
      }
    }
  };

  const canEdit = (ready || localReady) && !generating;
  const showInsights =
    waitingInputs ||
    status === CREATOR_SCRIPT_JOB_STATUS.conceptualising ||
    status === CREATOR_SCRIPT_JOB_STATUS.conceptualised ||
    ready;

  return {
    job,
    generating,
    generate,
    canEdit,
    analyzing,
    failed,
    waitingInputs,
    conceptualising: status === CREATOR_SCRIPT_JOB_STATUS.conceptualising,
    conceptualised: status === CREATOR_SCRIPT_JOB_STATUS.conceptualised,
    ready,
    concepts: job?.concepts || [],
    selectedConceptId: pickedConceptId || job?.scriptConceptId || "",
    contentAnalysis: showInsights ? deriveContentAnalysis(job?.creatorProfile?.aiAnalysis) : undefined,
    refetch,
    outputReady: ready || localReady,
    scriptSeed: [active.markdown, finishedScript(active.creator.jobId), scriptText(job?.script)].find(
      (script) => isSchemaScriptHtml(script)
    ) || "",
    rawScript: scriptText(job?.script),
    loadKey: `${active.id}:${
      isSchemaScriptHtml(active.markdown || finishedScript(active.creator.jobId)) ? "schema" : ready ? "pending" : "empty"
    }`,
  };
}
